<template>
  <b-modal
    id="editDirectionUniversityModal"
    size="lg"
    no-close-on-backdrop
    :title="title"
    @ok="onSave"
    @show="updateData"
  >
    <b-form-group label="Направление">
      <MultiSelect
        required
        :value="formData.educationDirection"
        :options="directions"
        label="codeTitle"
        trackBy="code"
        @input="onSelectDirection"
      />
    </b-form-group>
    <b-form-group label="Тип обучения">
      <b-form-radio-group
        v-model="formData.studyingForm"
        :options="studyingForms_"
        name="radios-stacked"
        stacked
      />
    </b-form-group>
    <b-form-group label="Доп. баллы">
      <div class="d-flex flex">
        <MultiSelect
          class="pr-2"
          :value="selectedOlympiad"
          :options="olympiads"
          label="title"
          @input="onSelectOlympiad"
        />
        <b-button
          variant="primary"
          class="align-self-center"
          :disabled="!selectedOlympiad || !selectedOlympiad.length"
          @click="addOlympiadToTable"
        >
          Добавить
        </b-button>
      </div>
      <div class="mt-2">
        <ag-grid-vue
          class="ag-theme-material h-100"
          :gridOptions="gridOptionsOlympic"
          :columnDefs="columnDefsOlympic"
          :rowData="formData.recomOlympiads"
        >
        </ag-grid-vue>
        <b-alert show v-if="isOlympiadNoMark" variant="danger" class="mt-2">
          Не указан дополнительный балл за олимпиаду
        </b-alert>
      </div>
    </b-form-group>
    <div class="d-flex flex-wrap my-2">
      <div class="mr-2">Предметы ЕГЭ</div>
      <b-button class="d-block ml-auto" @click="addEge" variant="primary">
        Добавить
      </b-button>
    </div>
    <div class="mt-2">
      <ag-grid-vue
        class="ag-theme-material h-100"
        :gridOptions="gridOptionsEge"
        :columnDefs="columnDefsEge"
        :rowData="formData.egeSubjects"
      >
      </ag-grid-vue>
    </div>
    <div class="d-flex flex-wrap my-2">
      <div class="mr-2">Проходные баллы и квота</div>
      <b-button class="d-block ml-auto" @click="addYear" variant="primary">
        Добавить
      </b-button>
    </div>
    <div class="mt-2">
      <ag-grid-vue
        class="ag-theme-material h-100"
        :gridOptions="gridOptionsHistory"
        :columnDefs="columnDefsHistory"
        :rowData="formData.directionMarkHistories"
      >
      </ag-grid-vue>
      <b-alert show v-if="isSameYears" variant="danger" class="mt-2">
        Года не должны повторяться
      </b-alert>
    </div>
    <EditDirectionExamModal
      :editData="selectedEge"
      :selectedExams="allSelectedEgeNames"
      @save="onSaveDirectionExam"
    />
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button @click="ok" variant="primary" :disabled="isDisabled">
        Сохранить
      </b-button>
      <b-button @click="cancel" variant="outline-primary">
        Отмена
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash'
import { AgGridVue } from 'ag-grid-vue'
import MultiSelect from '@/components/ui/MultiSelect.vue'
import { studyingForms } from '@/config/studying-forms'
import RecommendationAPI from '@/api/recommendation'
import AgGridFactory from '@/components/helpers/factory/agGrid'
import EditDirectionExamModal from '@/components/admin/modals/recommendations/EditDirectionExamModal.vue'

export default {
  name: 'EditDirectionUniversityModal',
  components: {
    MultiSelect,
    AgGridVue,
    EditDirectionExamModal
  },
  props: {
    editData: {
      type: Object,
      default: null
    }
  },
  watch: {
    editData: {
      deep: true,
      handler(data) {
        this.updateData(data)
      }
    }
  },
  data() {
    return {
      title: '',
      studyingForms_: studyingForms,
      addTitle: 'Добавление направления вуза',
      editTitle: 'Редактирование направления вуза',
      formData: {
        id: null,
        studyingForm: '',
        educationDirection: [],
        recomOlympiads: [],
        egeSubjects: [],
        directionMarkHistories: []
      },
      defaultOlympiads: [],
      selectedOlympiad: null,
      directions: [],
      gridOptionsOlympic: AgGridFactory.getGridOptionsDefault({
        domLayout: 'autoHeight',
        pagination: false
      }),
      gridOptionsHistory: AgGridFactory.getGridOptionsDefault({
        domLayout: 'autoHeight',
        pagination: false
      }),
      gridOptionsEge: AgGridFactory.getGridOptionsDefault({
        domLayout: 'autoHeight',
        pagination: false
      }),
      columnDefsOlympic: [
        {
          headerName: 'Олимпиада',
          field: 'title'
        },
        {
          headerName: 'Балл',
          field: 'olympiadDirection.addMark',
          editable: true,
          width: 100,
          suppressSizeToFit: true,
          cellEditor: 'NumericEditor',
          cellEditorParams: {
            nullable: true,
            min: 0,
            max: 100
          }
        },
        AgGridFactory.getDefaultActionRenderer({
          onDelete: this.onDeleteOlympicClick
        })
      ],
      columnDefsHistory: [
        {
          headerName: 'Год',
          field: 'year',
          number: true,
          editable: true,
          cellEditor: 'NumericEditor',
          cellEditorParams: {
            nullable: true
          }
        },
        {
          headerName: 'Балл',
          field: 'mark',
          editable: true,
          cellEditor: 'NumericEditor',
          cellEditorParams: {
            min: 0,
            nullable: true
          }
        },
        {
          headerName: 'Квота',
          field: 'quota',
          editable: true,
          cellEditor: 'NumericEditor',
          cellEditorParams: {
            nullable: true,
            min: 0
          }
        },
        AgGridFactory.getDefaultActionRenderer({
          onDelete: this.onDeleteYearClick
        })
      ],
      columnDefsEge: [
        {
          headerName: 'Номер',
          field: 'directionUniversitySubject.order',
          width: 100,
          suppressSizeToFit: true
        },
        {
          headerName: 'Название',
          field: 'name',
          width: 350
        },
        {
          headerName: 'Балл',
          field: 'directionUniversitySubject.minimalMark',
          width: 100,
          suppressSizeToFit: true
        },
        {
          headerName: 'Профильный',
          field: 'directionUniversitySubject.isProfile',
          valueFormatter: (params) => (params.value ? 'Да' : 'Нет'),
          width: 120,
          suppressSizeToFit: true,
          filterParams: {
            filterOptions: [
              'empty',
              {
                displayKey: 'yes',
                displayName: 'Да',
                test: (filterValue, cellValue) => cellValue === 'true',
                hideFilterInput: true
              },
              {
                displayKey: 'no',
                displayName: 'Нет',
                test: (filterValue, cellValue) => cellValue === 'false',
                hideFilterInput: true
              }
            ],
            suppressAndOrCondition: true
          }
        },
        AgGridFactory.getDefaultActionRenderer({
          onEdit: this.onEditEgeClick,
          onDelete: this.onDeleteEgeClick
        })
      ],
      selectedEge: null
    }
  },
  computed: {
    isDisabled() {
      return this.isSameYears || this.isOlympiadNoMark
        || !(this.formData && this.formData.educationDirection && this.formData.studyingForm)
    },
    olympiads() {
      const selectedIds = this.formData.recomOlympiads.map((ro) => ro.id)
      return this.defaultOlympiads.filter((o) => !selectedIds.includes(o.id))
    },
    allSelectedEgeNames() {
      return this.formData.egeSubjects.map((s) => s.name)
    },
    isOlympiadNoMark() {
      return this.formData.recomOlympiads.filter((ro) => !ro.olympiadDirection.addMark).length
    },
    isSameYears() {
      const dataWithYear = this.formData.directionMarkHistories.filter((h) => h.year)
      return dataWithYear.length !== _.uniqBy(dataWithYear, 'year').length
    }
  },
  async created() {
    const [olympiads, directions] = await Promise.all([
      RecommendationAPI.getOlympiads(),
      RecommendationAPI.getDirectionsByGroup()
    ])
    this.defaultOlympiads = olympiads.data
    this.directions = _.sortBy(directions.data, 'code')
      .map((dg) => ({ ...dg, codeTitle: `${dg.code} ${dg.title}` }))
    this.updateData()
  },
  methods: {
    updateData() {
      this.formData = _.cloneDeep(this.editData) || this.clearData()
      this.formData.educationDirection = this.editData && this.editData.educationDirection
        ? [this.directions.find((d) => d.id === this.editData.educationDirection.id)]
        : null
      this.title = this.editData ? this.editTitle : this.addTitle
    },
    onSelectDirection(data) {
      this.formData.educationDirection = [data]
    },
    onSelectOlympiad(data) {
      this.selectedOlympiad = [data]
    },
    onEditEgeClick(params) {
      this.selectedEge = params
      this.$bvModal.show('editDirectionExamModal')
    },
    onDeleteEgeClick(params) {
      const index = this.formData.egeSubjects.findIndex((s) => s.id === params.id)
      if (index > -1) {
        this.formData.egeSubjects.splice(index, 1)
      }
    },
    addEge() {
      this.selectedEge = null
      this.$bvModal.show('editDirectionExamModal')
    },
    addYear() {
      this.formData.directionMarkHistories.push({
        year: null,
        quota: null,
        mark: null
      })
    },
    addOlympiadToTable() {
      this.formData.recomOlympiads.push({
        ...this.selectedOlympiad[0],
        olympiadDirection: {
          addMark: 0
        }
      })
      this.selectedOlympiad = null
    },
    onSaveDirectionExam(params) {
      const index = this.selectedEge
        ? this.formData.egeSubjects.findIndex((s) => s.id === this.selectedEge.id)
        : -1
      if (index > -1) {
        this.formData.egeSubjects[index] = params
      } else {
        this.formData.egeSubjects.push(params)
      }
      this.formData.egeSubjects = [...this.formData.egeSubjects]
    },
    onSave() {
      this.$emit('save', {
        ...this.formData,
        directionMarkHistories: this.formData.directionMarkHistories.filter((d) => d.year),
        educationDirection: this.formData.educationDirection[0]
      })
    },
    onDeleteOlympicClick(params) {
      const index = this.formData.recomOlympiads.findIndex((o) => o.id === params.id)
      if (index > -1) {
        this.formData.recomOlympiads.splice(index, 1)
      }
    },
    onDeleteYearClick(params) {
      const index = this.formData.directionMarkHistories.findIndex((h) => _.isEqual(params, h))
      if (index > -1) {
        this.formData.directionMarkHistories.splice(index, 1)
      }
    },
    clearData() {
      return {
        id: null,
        studyingForm: '',
        educationDirection: null,
        recomOlympiads: [],
        egeSubjects: [],
        directionMarkHistories: []
      }
    }
  }
}
</script>

<style scoped>

</style>
